import { Location, NgClass } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, DestroyRef, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import '@kin/web-components/dist/external/kin-non-prod-banner/kin-non-prod-banner';
import { Subscription, filter } from 'rxjs';

import { AppConfigModule, AppContext } from './app-config';
import { AppGlobalLoadingContainerComponent } from './app-global-loader/app-global-loader.component';
import { AppLoaderService } from './app-global-loader/service/app-loader.service';
import { ContactDialogComponent } from './common/components/contact-dialog/contact-dialog.component';
import { FooterReviewComponent } from './common/components/footer/footer-review/footer-review.component';
import { HeaderComponent } from './common/components/header/header.component';
import { DebugTrayComponent } from './common/debug/debug-tray.component';
import { GoogleTagManagerService } from './common/services/analytics/google-tagmanager/gtm.service';
import { ContactDialogService } from './create-quote/services/contact-dialog/contact-dialog.service';
import { QuoteDraftStore } from './global-store/quote-draft';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, AppConfigModule, DebugTrayComponent, AppGlobalLoadingContainerComponent, ContactDialogComponent, FooterReviewComponent, NgClass],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppComponent implements OnInit, OnDestroy {
  @AppContext() private _appContext!: AppContext;
  private router = inject(Router);
  private location = inject(Location);
  private gtmService = inject(GoogleTagManagerService);
  private destroyRef = inject(DestroyRef);
  private routerSubscription: Subscription = new Subscription();
  private navigationSubscription: Subscription = new Subscription();
  private quoteDraftStore = inject(QuoteDraftStore);

  protected readonly currentYear: number = new Date().getFullYear();

  public loadingService = inject(AppLoaderService);
  public contactDialogService = inject(ContactDialogService);

  public notProd = this._appContext.notProd;
  public showDebugTray = false;
  public isLoading = false;
  public isComparePage = signal(false);
  public isReviewPage = signal(false);

  ngOnInit() {
    this.gtmService.init();

    this.routerSubscription = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.isComparePage.set(this.router.url.includes('/quote/compare'));
      this.isReviewPage.set(this.router.url.includes('/quote/review'));
    });

    // Prevent navigation if `quoteDraftStore.shouldDisallowBackwardsNavigation` is `true`.
    this.navigationSubscription = this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (event.navigationTrigger === 'popstate') {
          // 'popstate' means browser back/forward buttons were used.
          const allowedPaths = ['/quote/compare', '/quote/review'];
          const isAllowedNavigation = allowedPaths.includes(event.url);

          // Allow navigation if going to an allowed page, otherwise apply normal checks
          if (!isAllowedNavigation && this.quoteDraftStore.shouldDisallowBackwardsNavigation()) {
            this.location.forward(); // Cancel the back navigation; user stays on current page.
          }
        }
      });

    this.loadingService.isActive$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isActive) => {
      if (isActive) {
        this.isLoading = true;
      } else {
        this.isLoading = false;
      }
    });
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
    this.navigationSubscription.unsubscribe();
  }

  public toggleDebugTray() {
    this.showDebugTray = !this.showDebugTray;
  }
}
